/**
 * SSO (DSP / WDSSO) session store. Identity is kept in sessionStorage under `sso_*`
 * keys, isolated from the Cognito flow.
 */

import { SSO_STORAGE_KEYS } from './constants';

/** Extract a normalized identity from a DSP user profile payload. */
const normalizeIdentity = (profile = {}, fallbackUsername = '') => {
  const username =
    profile.username ||
    (Array.isArray(profile.uid) ? profile.uid[0] : profile.uid) ||
    fallbackUsername ||
    '';

  const email =
    (Array.isArray(profile.mail) ? profile.mail[0] : profile.mail) ||
    (Array.isArray(profile.email) ? profile.email[0] : profile.email) ||
    '';

  return {
    username,
    sub: username,
    email,
  };
};

/** Persist the SSO session established by initiateSsoLogin(). */
export const saveSsoSession = ({ username = '', profile = {} } = {}) => {
  const identity = normalizeIdentity(profile, username);

  sessionStorage.setItem(SSO_STORAGE_KEYS.USERNAME, identity.username);
  sessionStorage.setItem(SSO_STORAGE_KEYS.USER_SUB, identity.sub);
  sessionStorage.setItem(SSO_STORAGE_KEYS.USER_EMAIL, identity.email);
  sessionStorage.setItem(SSO_STORAGE_KEYS.USER_PROFILE, JSON.stringify(profile || {}));
  sessionStorage.setItem(SSO_STORAGE_KEYS.ACTIVE, 'true');
  sessionStorage.setItem(SSO_STORAGE_KEYS.AUTHENTICATED_AT, String(Date.now()));

  return identity;
};

/** @returns {string|null} resolved DSP username */
export const getSsoUsername = () => sessionStorage.getItem(SSO_STORAGE_KEYS.USERNAME);

/** @returns {string|null} stable SSO user identifier */
export const getSsoUserSub = () => sessionStorage.getItem(SSO_STORAGE_KEYS.USER_SUB);

/** @returns {string|null} SSO user email */
export const getSsoUserEmail = () => sessionStorage.getItem(SSO_STORAGE_KEYS.USER_EMAIL);

/** @returns {Object|null} full DSP user profile */
export const getSsoUserProfile = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SSO_STORAGE_KEYS.USER_PROFILE));
  } catch (e) {
    return null;
  }
};

/** @returns {boolean} whether an SSO session is active */
export const isSsoSessionActive = () =>
  sessionStorage.getItem(SSO_STORAGE_KEYS.ACTIVE) === 'true';

/** Remove all SSO session data. */
export const clearSsoSession = () => {
  Object.values(SSO_STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
};

export default {
  saveSsoSession,
  getSsoUsername,
  getSsoUserSub,
  getSsoUserEmail,
  getSsoUserProfile,
  isSsoSessionActive,
  clearSsoSession,
};
